export type PitchUsageType = "tourism" | "permanent" | "storage";

export const PITCH_USAGE_TYPE_OPTIONS: { value: PitchUsageType; label: string }[] = [
  { value: "tourism", label: "Tourismusplatz" },
  { value: "permanent", label: "Dauerstellplatz" },
  { value: "storage", label: "Abstellplatz" },
];

export function pitchUsageTypeLabel(value: PitchUsageType | string | null | undefined): string {
  return PITCH_USAGE_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? "Tourismusplatz";
}

export type NumericConstraintField =
  | "vehicle_length_m"
  | "vehicle_width_m"
  | "vehicle_height_m"
  | "vehicle_weight_kg";

export type VehicleFieldConstraint = {
  min: string;
  max: string;
};

export type ArrivalTypePitchSettings = {
  allowed: boolean;
  constraints: Partial<Record<NumericConstraintField, VehicleFieldConstraint>>;
};

export type PitchArrivalTypeSettingsForm = Record<string, ArrivalTypePitchSettings>;

export const NUMERIC_CONSTRAINT_FIELDS: { key: NumericConstraintField; label: string }[] = [
  { key: "vehicle_length_m", label: "Länge (m)" },
  { key: "vehicle_width_m", label: "Breite (m)" },
  { key: "vehicle_height_m", label: "Höhe (m)" },
  { key: "vehicle_weight_kg", label: "Gewicht (kg)" },
];

export function emptyArrivalTypeSettings(
  arrivalTypes: { code: string }[],
): PitchArrivalTypeSettingsForm {
  const result: PitchArrivalTypeSettingsForm = {};
  for (const type of arrivalTypes) {
    result[type.code] = { allowed: false, constraints: {} };
  }
  return result;
}

export function arrivalSettingsFromApi(
  raw: Record<string, { constraints?: Record<string, { min?: string | number | null; max?: string | number | null }> }> | null | undefined,
  arrivalTypes: { code: string }[],
): PitchArrivalTypeSettingsForm {
  const base = emptyArrivalTypeSettings(arrivalTypes);
  if (!raw) return base;
  for (const [code, entry] of Object.entries(raw)) {
    if (!base[code]) continue;
    base[code].allowed = true;
    const constraints = entry?.constraints ?? {};
    for (const [fieldKey, bounds] of Object.entries(constraints)) {
      if (!NUMERIC_CONSTRAINT_FIELDS.some((f) => f.key === fieldKey)) continue;
      base[code].constraints[fieldKey as NumericConstraintField] = {
        min: bounds.min != null ? String(bounds.min) : "",
        max: bounds.max != null ? String(bounds.max) : "",
      };
    }
  }
  return base;
}

export function arrivalSettingsToApi(
  form: PitchArrivalTypeSettingsForm,
  restrictArrivalTypes: boolean,
): Record<string, { constraints: Record<string, { min?: number; max?: number }> }> {
  if (!restrictArrivalTypes) return {};
  const result: Record<string, { constraints: Record<string, { min?: number; max?: number }> }> = {};
  for (const [code, entry] of Object.entries(form)) {
    if (!entry.allowed) continue;
    const constraints: Record<string, { min?: number; max?: number }> = {};
    for (const [fieldKey, bounds] of Object.entries(entry.constraints)) {
      const min = bounds.min.trim() ? Number(bounds.min.replace(",", ".")) : undefined;
      const max = bounds.max.trim() ? Number(bounds.max.replace(",", ".")) : undefined;
      if (min === undefined && max === undefined) continue;
      constraints[fieldKey] = {
        ...(min !== undefined && Number.isFinite(min) ? { min } : {}),
        ...(max !== undefined && Number.isFinite(max) ? { max } : {}),
      };
    }
    result[code] = { constraints };
  }
  return result;
}

export function hasArrivalTypeRestrictions(
  raw: Record<string, unknown> | null | undefined,
): boolean {
  return Boolean(raw && Object.keys(raw).length > 0);
}
